import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Typography,
    Box,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';

interface CareerOpening {
    id: number;
    is_published: boolean;
    position_display_title: string;
    position_title: string;
    position_location: string;
    position_availability: string;
    position_description: string;
    position_purpose: string;
    position_requirements: string;
    position_ideal_candidate: string;
    position_duties: string;
    position_contact_instructions: string;
    created_at: string;
    created_by: string;
}

interface CareersAccordionProps {
    careersData: CareerOpening[];
    renderHTMLContent?: (htmlString: string) => React.ReactNode;
}

const CareersAccordion: React.FC<CareersAccordionProps> = ({ careersData, renderHTMLContent }) => {
    const [expanded, setExpanded] = useState<string | false>(false);

    const handleChange =
      (panel: string) =>
        (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const renderContent = (content: string) => {
        if (!content) return null;

        const hasHTMLTags = /<[^>]*>/.test(content);

        if (hasHTMLTags && renderHTMLContent) {
            return (
              <Box sx={{ fontSize: 16, color: 'rgba(71, 84, 103, 1)', mb: 2 }}>
                  {renderHTMLContent(content)}
              </Box>
            );
        }

        return (
          <Typography sx={{ fontSize: 16, color: 'rgba(71, 84, 103, 1)', mb: 2, whiteSpace: 'pre-wrap' }}>
              {content}
          </Typography>
        );
    };

    if (!careersData || careersData.length === 0) {
        return null;
    }

    return (
      <>
          {careersData
            .filter(job => job.is_published)
            .sort((a, b) =>
              a.position_display_title.toLowerCase().localeCompare(b.position_display_title.toLowerCase())
            )
            .map((job) => {
                const panelId = `panel-${job.id}`;
                return (
                  <Accordion
                    key={job.id}
                    elevation={0}
                    expanded={expanded === panelId}
                    onChange={handleChange(panelId)}
                    sx={{ width: '100%' }}
                  >
                      <AccordionSummary
                        expandIcon={
                            expanded === panelId ? (
                              <RemoveCircleOutlineOutlinedIcon />
                            ) : (
                              <AddCircleOutlineOutlinedIcon />
                            )
                        }
                        aria-controls={`${panelId}-content`}
                        id={`${panelId}-header`}
                        sx={{
                            borderBottom:
                              expanded === panelId
                                ? 'none'
                                : '#000 solid 1px',
                        }}
                      >
                          <Typography
                            variant='h1'
                            sx={{
                                fontSize: '32px',
                                fontWeight: '500',
                                lineHeight: '28px',
                                color: '#101828',
                                mt: 1,
                            }}
                          >
                              {job.position_display_title}
                          </Typography>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'center',
                            borderBottom: '#000 1px solid',
                        }}
                      >
                          <Grid container spacing={4}>
                              <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <Typography
                                        sx={{
                                            color: 'rgba(16, 24, 40, 1)',
                                            fontSize: '24px',
                                            fontFamily: 'Gill Sans',
                                        }}
                                      >
                                          Position
                                      </Typography>
                                      {renderContent(job.position_title)}

                                      <Typography
                                        sx={{
                                            color: 'rgba(16, 24, 40, 1)',
                                            fontSize: '24px',
                                            fontFamily: 'Gill Sans',
                                        }}
                                      >
                                          Area
                                      </Typography>
                                      {renderContent(job.position_location)}

                                      <Typography
                                        sx={{
                                            color: 'rgba(16, 24, 40, 1)',
                                            fontSize: '24px',
                                            fontFamily: 'Gill Sans',
                                        }}
                                      >
                                          Position available from
                                      </Typography>
                                      {renderContent(job.position_availability)}

                                      {job.position_description && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                Description
                                            </Typography>
                                            {renderContent(job.position_description)}
                                        </>
                                      )}

                                      {job.position_purpose && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                Main purpose of the job
                                            </Typography>
                                            {renderContent(job.position_purpose)}
                                        </>
                                      )}

                                      {job.position_requirements && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                Minimum requirements
                                            </Typography>
                                            {renderContent(job.position_requirements)}
                                        </>
                                      )}

                                      {job.position_ideal_candidate && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                Ideal candidate
                                            </Typography>
                                            {renderContent(job.position_ideal_candidate)}
                                        </>
                                      )}

                                      {job.position_duties && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                Main duties
                                            </Typography>
                                            {renderContent(job.position_duties)}
                                        </>
                                      )}

                                      {job.position_contact_instructions && (
                                        <>
                                            <Typography
                                              sx={{
                                                  color: 'rgba(16, 24, 40, 1)',
                                                  fontSize: '24px',
                                                  fontFamily: 'Gill Sans',
                                              }}
                                            >
                                                How to Apply
                                            </Typography>
                                            {renderContent(job.position_contact_instructions)}
                                        </>
                                      )}
                                  </Box>
                              </Grid>
                          </Grid>
                      </AccordionDetails>
                  </Accordion>
                );
            })}
      </>
    );
};

export default CareersAccordion;